import { z } from "zod"
import { TranslationKey } from "../i18n/translation-key"
import { DamperBookingInfo } from "./damper-service"
import { Discount, LegacyCompatibleDiscountSchema } from "./discount"
import {
  EvaluatedPartDuringProperInspectionSchema,
  EvaluatedServiceBikePart,
  EvaluatedServiceBikePartSchema,
  ServicePartWithAllInfo,
  WithOptionalGradeAndAction,
} from "./evaluated-service-bike-part"
import { DiscountCodeOnJob } from "./job"
import { ActiveSecondsSchema, InteractionEffortSchema } from "./job-timing"
import {
  ManualServiceAction,
  ManualServiceActionSchema,
} from "./manual-service-action"
import { Mechanic } from "./mechanic"
import { PartActionUrgency } from "./part-action-urgency"
import { PartGrade, PartGradeUtils } from "./part-grade"
import { PartLastVisit } from "./part-last-visit"
import { SaveServiceBikeSchema, ServiceBike } from "./service-bike"
import { ServiceBikePartWithStoreSettings } from "./service-bike-part-with-store-settings"
import { ServiceFee } from "./service-fee"
import { ServicePackage } from "./service-package"
import { ServiceAction } from "./service-part-action"
import {
  ServiceWash,
  ServiceWashSchema,
  ServiceWashSnapshotSchema,
  WashAlternativesGroup,
} from "./service-wash"
import { EquipmentAnswerSchema, UpsellInspectionData } from "./upsell"

export interface DataRequiredForProperInspection {
  receivingMechanicId: string
  assignedMechanicId: string | null
  /** Logged-in user's linked Personal, else the assigned mechanic, else the receiver */
  defaultInspectionMechanicId: string
  bikeInfoFromReception: ServiceBike
  serviceParts: ServiceBikePartWithStoreSettings[]
  customerBudget: number
  availableServicePackages: ServicePackage[]
  availableFees: ServiceFee[]
  availableWashes: ServiceWash[]
  // A resumed draft may hold a component only marked as not working, with
  // neither grade nor action yet.
  evaluatedParts: (WithOptionalGradeAndAction<EvaluatedServiceBikePart> & {
    name: string
    image: string
    // Only a saved draft carries this. Components straight from the reception
    // always have a real action, so the CRM's dirty test sees them anyway.
    isDirty?: boolean
    isMassGraded?: boolean
    /**
     * The "Nu"/"Inom 6 mån" pick the inspection starts with: the draft's
     * pick, otherwise "Nu".
     */
    urgencyPick: PartActionUrgency
  })[]
  selectedFeesFromReception: { id: string; name: string }[]
  selectedWashFromReception: {
    originalWashId: string
    isMandatory: boolean
    laborPriceDiscount: Discount | null
  } | null
  manualAddonServicesFromReception: ManualServiceAction[]
  packageDiscountsFromReception: Record<string, Discount | null>
  selectedPackagesFromReception: {
    id: string
    includedParts: { id: string; action: ServiceAction }[]
  }[]
  orderDiscountFromJob: Discount | null
  fullApprovalOrderDiscountFromJob: Discount | null
  laborDiscountFromJob: Discount | null
  productsDiscountFromJob: Discount | null
  discountCodeFromJob: DiscountCodeOnJob | null
  damperBookingInfos: DamperBookingInfo[]
  checkInComment: string | null
  draftDependencies?: {
    partDeps: Record<
      string,
      {
        requiredWashId?: string
        requiredPartIds: string[]
        requiredManualServiceActionIds: string[]
      }
    >
    washDeps: {
      requiredPartIds: string[]
      requiredManualServiceActionIds: string[]
    }
    manualServiceActionDeps: Record<
      string,
      {
        requiredWashId?: string
        requiredPartIds: string[]
        requiredManualServiceActionIds: string[]
      }
    >
  }
  washAlternativesFromDraft?:
    | WashAlternativesGroup
    | DraftWashAlternatives
    | null
  /** The draft's removals, otherwise those carried over from the last visit. */
  initialRemovedPartIds: string[]
  /** Removed at the bike's last inspection, for the "removed" notice. */
  removedPartIdsFromLastVisit: string[]
  /** The bike's history per component. Empty on its first visit. */
  partLastVisits?: PartLastVisit[]
  lastVisitDate?: string | null
  messageToCustomerFromDraft: string | null
  upsell: UpsellInspectionData
}

export const ServiceWashInProperInspectionSchema =
  ServiceWashSnapshotSchema.pick({
    originalWashId: true,
    isMandatory: true,
    requiredPartIds: true,
    requiredManualServiceActionIds: true,
  }).extend({
    laborPriceDiscount: LegacyCompatibleDiscountSchema.nullable().default(null),
  })

const WashAlternativeInProperInspectionSchema = z.object({
  originalWashId: z.string().uuid(),
  isRecommended: z.boolean(),
  laborPriceDiscount: LegacyCompatibleDiscountSchema.nullable().default(null),
})

export const WashAlternativesInProperInspectionSchema = z.object({
  alternatives: z.array(WashAlternativeInProperInspectionSchema).min(1),
  isMandatory: z.boolean(),
  comment: z.string().nullable(),
  requiredPartIds: z.array(z.string().uuid()),
  requiredManualServiceActionIds: z.array(z.string().uuid()),
})

export type WashAlternativesInProperInspection = z.infer<
  typeof WashAlternativesInProperInspectionSchema
>

export const SubmitProperInspectionSchema = z
  .object({
    evaluatedParts: z.array(EvaluatedPartDuringProperInspectionSchema),
    /** Components the mechanic took out, remembered for the bike's next visit. */
    removedPartIds: z.array(z.string().uuid()).default([]),
    mechanicId: z.string().uuid(),
    bike: SaveServiceBikeSchema.omit({
      publishedToReRideAt: true,
      shopifyProductId: true,
    }),
    wash: ServiceWashInProperInspectionSchema.nullable(),
    washAlternatives: WashAlternativesInProperInspectionSchema.nullable(),
    feeIds: z.array(z.string().uuid()),
    manualServiceActions: z.array(ManualServiceActionSchema),
    sendProposalToCustomer: z.boolean(),
    messageToCustomer: z.string().nullable(), // Can be null only if sendProposalToCustomer is false
    packageDiscounts: z
      .record(z.string().uuid(), LegacyCompatibleDiscountSchema.nullable())
      .optional(),
    orderDiscount: LegacyCompatibleDiscountSchema.nullable().optional(),
    fullApprovalOrderDiscount:
      LegacyCompatibleDiscountSchema.nullable().optional(),
    laborDiscount: LegacyCompatibleDiscountSchema.nullable().optional(),
    productsDiscount: LegacyCompatibleDiscountSchema.nullable().optional(),
    discountCodeId: z.string().uuid().nullable().optional(),
    selectedPackageIds: z.array(z.string().uuid()).optional(),
    sendViaSms: z.boolean().optional(),
    sendViaEmail: z.boolean().optional(),
    // Whether the customer gets the proposal text in the SMS itself rather than
    // a teaser linking to the förslag. Absent means true, like sendViaSms
    // above: this flow has always sent the full text, and flipping it would
    // silently stop customers receiving the proposal they are asked to answer.
    // The API sends the full text regardless when it fits in one segment.
    sendFullSms: z.boolean().optional(),
    extraPackagePartActions: z
      .array(
        z.object({
          packageId: z.string().uuid(),
          partId: z.string().uuid(),
        }),
      )
      .default([]),
    /** Answers from the "Utrustning" step. */
    equipment: z.array(EquipmentAnswerSchema).optional(),
    activeSeconds: ActiveSecondsSchema.optional(),
    effort: InteractionEffortSchema.optional(),
  })
  .refine(
    (data) => {
      if (!data.sendProposalToCustomer) return true
      return data.sendViaSms !== false || data.sendViaEmail === true
    },
    {
      message: "common.validation.channelRequired" satisfies TranslationKey,
      path: ["sendViaSms"],
    },
  )
  // Drafts and previews may hold ungraded work; a finished inspection only
  // leaves out grades the action doesn't need.
  .refine(
    (data) => !data.evaluatedParts.some(PartGradeUtils.hasActionWithoutGrade),
    { path: ["evaluatedParts"] },
  )

export type SubmitProperInspection = z.infer<
  typeof SubmitProperInspectionSchema
>

const EvaluatedPartForDraftSchema = EvaluatedServiceBikePartSchema.extend({
  // Null while the mechanic hasn't graded an action yet (e.g. one agreed at
  // the reception). Defaulted so older drafts still parse.
  grade: z.nativeEnum(PartGrade).nullable().default(null),
  // Null for a component the mechanic only marked as not working, so the
  // mark survives until they pick an action.
  recommendedAction: z.nativeEnum(ServiceAction).nullable(),
  requiredWashId: z.string().uuid().nullable().default(null),
  requiredPartIds: z.array(z.string().uuid()).default([]),
  requiredManualServiceActionIds: z.array(z.string().uuid()).default([]),
  requiresPhysicalWorkshop: z.boolean().default(false),
  // Whether the mechanic put something into this component. Only the draft
  // carries it — the finished inspection has no use for it, and a component
  // in a reception always has a real action, which is signal enough.
  isDirty: z.boolean().default(false),
  // Set for every component at once, so it doesn't count towards quality.
  isMassGraded: z.boolean().default(false),
})

const DraftWashAlternativeSchema = z.object({
  originalWashId: z.string().uuid(),
  name: z.string(),
  price: z.number(),
  laborPriceDiscount: LegacyCompatibleDiscountSchema.nullable().default(null),
  isRecommended: z.boolean(),
})

const DraftWashAlternativesSchema = z.object({
  alternatives: z.array(DraftWashAlternativeSchema),
  isMandatory: z.boolean(),
  comment: z.string().nullable(),
})

export type DraftWashAlternatives = z.infer<typeof DraftWashAlternativesSchema>

export const ProperInspectionDraftSchema = z.object({
  evaluatedParts: z.array(EvaluatedPartForDraftSchema),
  bike: SaveServiceBikeSchema.omit({
    publishedToReRideAt: true,
    shopifyProductId: true,
  }),
  // Drafts are stored as JSON, many from before washes had minutes; the
  // estimate reads them from the wash itself, so a draft never needs them.
  wash: ServiceWashSchema.omit({ durationMinutes: true }).nullable(),
  washAlternatives: DraftWashAlternativesSchema.nullable().optional(),
  feeIds: z.array(z.string().uuid()),
  manualServiceActions: z.array(ManualServiceActionSchema),
  packageDiscounts: z
    .record(z.string().uuid(), LegacyCompatibleDiscountSchema.nullable())
    .optional(),
  orderDiscount: LegacyCompatibleDiscountSchema.nullable().optional(),
  fullApprovalOrderDiscount:
    LegacyCompatibleDiscountSchema.nullable().optional(),
  laborDiscount: LegacyCompatibleDiscountSchema.nullable().optional(),
  productsDiscount: LegacyCompatibleDiscountSchema.nullable().optional(),
  discountCodeId: z.string().uuid().nullable().optional(),
  messageToCustomer: z.string().nullable().optional(),
  washRequiredPartIds: z.array(z.string().uuid()).default([]),
  washRequiredManualServiceActionIds: z.array(z.string().uuid()).default([]),
  removedPartIds: z.array(z.string().uuid()).default([]),
  // Absent on drafts saved before packages were stored with the draft; the
  // inspection form then falls back to the job's package tasks.
  selectedPackageIds: z.array(z.string().uuid()).optional(),
  // Defaulted so drafts (and sent-inspection snapshots) saved before the
  // "Utrustning" step existed still parse.
  equipment: z.array(EquipmentAnswerSchema).optional(),
  // Active seconds since the form was opened or last saved. Not part of the
  // stored draft; the API adds it to the job's inspection time.
  activeSeconds: ActiveSecondsSchema.optional(),
  // Clicks, scroll and so on over the same period, stored as TaskEffort.
  effort: InteractionEffortSchema.optional(),
})

export type ProperInspectionDraft = z.infer<typeof ProperInspectionDraftSchema>

export type ExtendedInspectionDraft = Omit<
  ProperInspectionDraft,
  "evaluatedParts"
> & {
  evaluatedParts: WithOptionalGradeAndAction<ServicePartWithAllInfo>[]
  washRequiredPartIds: string[]
  washRequiredManualServiceActionIds: string[]
}

export enum ResetToSentInspectionBlockReason {
  /** No service proposal from an inspection has been sent for the job. */
  NO_SENT_PROPOSAL = "NO_SENT_PROPOSAL",
  /** A Tilläggsorder has been sent after the inspection. */
  ADDON_ORDER_SENT = "ADDON_ORDER_SENT",
  /** The job is on an invoice. */
  INVOICED = "INVOICED",
}

export interface ProperInspection {
  mechanic: Mechanic
}

export interface PartCommentHistoryEntry {
  id: string
  comment: string
  recommendedAction: ServiceAction
  createdAt: string
}

export interface PartCommentHistoryPage {
  comments: PartCommentHistoryEntry[]
  hasMore: boolean
}
